// ledgerDemoSource.js
//
// Owns: (a) demo-tenant detection, (b) demo-mode flag, (c) fixture builders
// derived from POSITIONS (multi-investor), (d) in-memory discard state, (e) ledgerFetch shim.
//
// The synthetic fixture is derived entirely from ledgerDemoData.js and demoPortfolio.js —
// no hand-transcribed numbers. Numbers tie by construction via foldFund/buildDocTimeline.

import { foldFund, buildDocTimeline } from "./demoPortfolio";
import { POSITIONS, BLENDED_IRR, DEMO_REPORT_INVESTOR } from "./ledgerDemoData";

// ── Demo-tenant detection ─────────────────────────────────────────────────────

import { DEMO_TENANT_ID as LEDGER_DEMO_TENANT_ID } from "./demo";
export { LEDGER_DEMO_TENANT_ID };
export const isLedgerDemoUser = (user) => user?.tenant_id === LEDGER_DEMO_TENANT_ID;

// ── Demo-mode flag — set by the page before any fetch; ledgerFetch reads it ──

let _demoMode = false;
export const setLedgerDemoMode = (v) => { _demoMode = !!v; };

// ── In-memory discard set, keyed `${portal}|${fund_name}|${investor_name}` ──

const _discarded = new Set();
export const __resetLedgerDemoState = () => { _discarded.clear(); };  // for tests

// ── Kit constants matching backend/services/ledger_kits.py EXACTLY ───────────

const PE_KIT_FE = {
  class: "PE",
  label: "Private Equity",
  columns: [
    { id: "commitment_total",              label: "Commitment",    kind: "money", show: "has_real_unfunded" },
    { id: "uncalled",                      label: "Uncalled",      kind: "money", show: "has_real_unfunded" },
    { id: "capital_contributed_cumulative", label: "Invested",      kind: "money", show: "always" },
    { id: "distributions_cumulative",      label: "Distributed",   kind: "money", show: "always" },
    { id: "nav",                           label: "Current Value", kind: "money", show: "always", as_of: "nav_as_of" },
    { id: "tvpi",                          label: "Net multiple",  kind: "ratio", show: "has_contributed" },
    { id: "irr",                           label: "Return %",      kind: "pct",   show: "irr_reliable" },
  ],
};

const RE_KIT_FE = {
  class: "RE",
  label: "Real Estate",
  columns: [
    { id: "capital_contributed_cumulative", label: "Invested",        kind: "money", show: "always" },
    { id: "distributions_cumulative",       label: "Distributed",     kind: "money", show: "always" },
    { id: "nav",                            label: "Current Value",   kind: "money", show: "always", as_of: "nav_as_of" },
    { id: "tvpi",                           label: "Equity Multiple", kind: "ratio", show: "has_contributed" },
    { id: "dpi",                            label: "Capital Returned",kind: "ratio", show: "has_distributions" },
    { id: "irr",                            label: "Return %",        kind: "pct",   show: "irr_reliable" },
  ],
};

// ── doctype normalizer ────────────────────────────────────────────────────────

export function normalizeDocType(raw) {
  if (!raw) return raw;
  if (raw.startsWith("Capital Call"))  return "Capital Call Notice";
  if (raw.startsWith("Distribution"))  return "Distribution Notice";
  if (raw === "Subscription Confirmation") return "Subscription Confirmation";
  if (raw === "Capital Account Statement") return "Capital Account Statement";
  if (raw === "Quarterly Statement")       return "Quarterly Statement";
  return raw;
}

// ── buildMember — converts a POSITIONS entry into a ledger member row ─────────

function buildMember(pos) {
  const fold = foldFund(pos);
  const live = pos.events.filter((e) => !e.synthetic);
  const dates = live.map((e) => e.date).sort();
  const navSnaps = live.filter((e) => e.type === "nav_snapshot");
  const navAsOf = navSnaps.length ? navSnaps.map((e) => e.date).sort().at(-1) : null;
  const doctypes = [...new Set(live.map((e) => normalizeDocType(e.docType)).filter(Boolean))];
  const key = `${pos.portal}|${pos.fundName}|${pos.investorName}`;

  return {
    portal: pos.portal,
    fund_name: pos.fundName,
    investor_name: pos.investorName,
    commitment_total: fold.commitment,
    capital_called_cumulative: fold.called,
    capital_contributed_cumulative: fold.called,
    distributions_cumulative: fold.distributed,
    tax_withheld_cumulative: null,
    nav: fold.nav,
    nav_as_of: navAsOf,
    commitment_date: pos.events.find((e) => e.type === "commitment")?.date ?? null,
    is_exited: false,
    exit_date: null,
    currency: pos.currency,
    uncalled: fold.unfunded,
    tvpi: fold.tvpi,
    dpi: fold.dpi,
    rvpi: fold.rvpi,
    irr: pos.irrReliable ? pos.irr : null,
    irr_reliable: !!pos.irrReliable,
    discarded: _discarded.has(key),
    position_state: "active",
    source_doctypes: doctypes,
    first_activity: dates[0] ?? null,
    last_activity: dates.at(-1) ?? null,
  };
}

// ── ledgerDemoOverview — returns a fresh object each call ─────────────────────

export function ledgerDemoOverview() {
  // Group positions by (portal, fundName)
  const portalFundMap = new Map(); // portal -> Map(fundName -> [pos])
  for (const pos of POSITIONS) {
    if (!portalFundMap.has(pos.portal)) portalFundMap.set(pos.portal, new Map());
    const fundMap = portalFundMap.get(pos.portal);
    if (!fundMap.has(pos.fundName)) fundMap.set(pos.fundName, []);
    fundMap.get(pos.fundName).push(pos);
  }

  const portals = [];
  const allMembers = [];

  for (const [portal, fundMap] of portalFundMap) {
    const portalFunds = [];

    for (const [fundName, positions] of fundMap) {
      // Build members, sorted DESC by nav
      const members = positions.map(buildMember).sort((a, b) => {
        const av = a.nav ?? -Infinity;
        const bv = b.nav ?? -Infinity;
        return bv - av;
      });

      allMembers.push(...members);

      const kit = positions[0].assetClass === "RE" ? RE_KIT_FE : PE_KIT_FE;
      const totalNav = members.some((m) => m.nav != null)
        ? members.reduce((s, m) => s + (m.nav ?? 0), 0)
        : null;

      const fundNode = {
        fund_name: fundName,
        currency: positions[0].currency,
        portal,
        kit,
        total_commitment: members.reduce((s, m) => s + (m.commitment_total ?? 0), 0),
        total_distributed: members.reduce((s, m) => s + (m.distributions_cumulative ?? 0), 0),
        total_nav: totalNav,
        members,
      };
      portalFunds.push(fundNode);
    }

    // Sort funds within portal DESC by total_nav
    portalFunds.sort((a, b) => {
      const av = a.total_nav ?? -Infinity;
      const bv = b.total_nav ?? -Infinity;
      return bv - av;
    });

    const portalTotalNav = portalFunds.some((f) => f.total_nav != null)
      ? portalFunds.reduce((s, f) => s + (f.total_nav ?? 0), 0)
      : null;

    portals.push({ portal, total_nav: portalTotalNav, funds: portalFunds });
  }

  // Sort portals DESC by total_nav (null sorts last)
  portals.sort((a, b) => {
    const av = a.total_nav ?? -Infinity;
    const bv = b.total_nav ?? -Infinity;
    return bv - av;
  });

  // Build investors summary
  const investorMap = new Map();
  for (const member of allMembers) {
    if (!investorMap.has(member.investor_name)) {
      investorMap.set(member.investor_name, { positions: [], currencies: new Set() });
    }
    const inv = investorMap.get(member.investor_name);
    inv.positions.push(member);
    inv.currencies.add(member.currency);
  }

  const investors = [...investorMap.entries()].map(([investor_name, { positions, currencies }]) => ({
    investor_name,
    positions,
    total_commitment: positions.reduce((s, m) => s + (m.commitment_total ?? 0), 0),
    total_contributed: positions.reduce((s, m) => s + (m.capital_contributed_cumulative ?? 0), 0),
    total_distributed: positions.reduce((s, m) => s + (m.distributions_cumulative ?? 0), 0),
    total_nav: positions.reduce((s, m) => s + (m.nav ?? 0), 0),
    blended_irr: BLENDED_IRR[investor_name] ?? null,
    _currencies: currencies, // internal, used below for mixed-currency check
  })).sort((a, b) => b.total_nav - a.total_nav);

  // Find mixed-currency investors (>1 distinct currency)
  const mixedCurrencyInvestors = investors
    .filter((inv) => inv._currencies.size > 1)
    .map((inv) => inv.investor_name);

  // Clean up internal field
  for (const inv of investors) {
    delete inv._currencies;
  }

  const numPortals = portals.length;
  const numFunds = portals.reduce((s, p) => s + p.funds.length, 0);
  const numMembers = allMembers.length;
  const numInvestors = investors.length;
  const totalDocs = POSITIONS.reduce((s, p) => s + p.events.length, 0);

  const reliableIrr = allMembers.filter((m) => m.irr_reliable === true);
  const irrReliableCount = reliableIrr.length;
  const irrTotalCount = allMembers.length;

  const qa = {
    tenant_id: "elementar_demo",
    portal: null,
    verdict: "WARN",
    coverage: { positions: numMembers, funds: numFunds, investors: numInvestors },
    fold_reproducible: true,
    empty_positions: [],
    mixed_currency_investors: mixedCurrencyInvestors,
    irr_reliable: { reliable: irrReliableCount, total: irrTotalCount },
    disagreements_unreviewed: 0,
    quarantined_unreviewed: 0,
    checks: [
      { name: "fold_reproducible", status: "ok",   detail: "Fold is reproducible." },
      { name: "empty_positions",   status: "ok",   detail: "No empty positions." },
      { name: "mixed_currency",    status: "warn", detail: `${mixedCurrencyInvestors.length} investor(s) hold mixed-currency positions.` },
      { name: "irr_reliable",      status: "ok",   detail: `${irrReliableCount} of ${irrTotalCount} positions have a reliable IRR.` },
      { name: "disagreements",     status: "ok",   detail: "No unreviewed disagreements." },
      { name: "quarantined",       status: "ok",   detail: "Nothing quarantined." },
    ],
  };

  // filter_options: union across all live (non-synthetic) events
  const allLiveEvents = POSITIONS.flatMap((p) => p.events.filter((e) => !e.synthetic));
  const allDoctypes = [...new Set(allLiveEvents.map((e) => normalizeDocType(e.docType)).filter(Boolean))].sort();
  const allCurrencies = [...new Set(POSITIONS.map((p) => p.currency))].sort();
  const allAssetClasses = [...new Set(portals.flatMap((p) => p.funds.map((f) => f.kit.class)))].sort();

  const filter_options = {
    doctypes: allDoctypes,
    currencies: allCurrencies,
    asset_classes: allAssetClasses,
  };

  return {
    tenant_id: "elementar_demo",
    editable: true, // demo shim implements discard/restore locally — keep controls live
    data_as_of: null,
    qa,
    portals,
    investors,
    disagreements: [],
    counts: {
      portals: numPortals,
      funds: numFunds,
      investors: numInvestors,
      positions: numMembers,
      documents: totalDocs,
      disagreements: 0,
      quarantined: 0,
    },
    filter_options,
  };
}

// ── ledgerDemoTimeline ────────────────────────────────────────────────────────

export function ledgerDemoTimeline(fundName, investorName) {
  const pos = POSITIONS.find(
    (p) => p.fundName === fundName && (!investorName || p.investorName === investorName)
  );
  if (!pos) return { documents: [], points: [] };
  return { ...buildDocTimeline(pos), points: buildPoints(pos) };
}

// Mirrors services/v4_bridge.py::timeline points: amounts are positive floats for
// both calls and distributions (the chart draws direction); a value point carries the NAV.
// Commitments, synthetic duplicates and rejected events are not points. No doc_hash /
// download_url: the demo serves no blobs, PositionTimeline shows the filename as text.
const _POINT_KIND = { capital_call: "call", distribution: "distribution", nav_snapshot: "value" };

function buildPoints(pos) {
  const pts = pos.events
    .filter((e) => _POINT_KIND[e.type] && !e.synthetic && !e.rejected)
    .map((e) => ({
      date: e.date,
      kind: _POINT_KIND[e.type],
      amount: Number(e.type === "nav_snapshot" ? e.nav : e.amount),
      doc_hash: null,
      filename: e.source_pdf || null,
      download_url: null,
      quote: null,
      why: null,
      tip: false,
    }))
    .filter((p) => Number.isFinite(p.amount))
    .sort((a, b) => a.date.localeCompare(b.date) || a.kind.localeCompare(b.kind));
  const lastValue = pts.map((p) => p.kind).lastIndexOf("value");
  if (lastValue >= 0) pts[lastValue].tip = true;
  return pts;
}

// ── ledgerDemoDiscarded ───────────────────────────────────────────────────────

export function ledgerDemoDiscarded() {
  return {
    discarded: [..._discarded].map((k) => {
      const [portal, fund_name, investor_name] = k.split("|");
      return { portal, fund_name, investor_name, updated_at: new Date().toISOString(), updated_by: "demo" };
    }),
  };
}

// ── ledgerDemoDiscard ─────────────────────────────────────────────────────────

export function ledgerDemoDiscard({ portal, fund_name, investor_name, discarded }) {
  const key = `${portal}|${fund_name}|${investor_name}`;
  if (discarded) _discarded.add(key); else _discarded.delete(key);
  return {
    tenant_id: "elementar_demo",
    portal,
    fund_name,
    investor_name,
    discarded: !!discarded,
    updated_at: new Date().toISOString(),
    updated_by: "demo",
  };
}

// ── Report helpers (for the demo Render Report button) ───────────────────────

export { DEMO_REPORT_INVESTOR };

export function getDemoReportPortfolio(investorName = DEMO_REPORT_INVESTOR) {
  const funds = POSITIONS.filter((p) => p.investorName === investorName)
    .map((p) => ({ id: p.id, portal: p.portal, fundName: p.fundName, currency: p.currency, events: p.events }));
  return { investor: investorName, funds };
}

export function getDemoReportFundNames(investorName = DEMO_REPORT_INVESTOR) {
  return POSITIONS.filter((p) => p.investorName === investorName).map((p) => p.fundName);
}

// ── Lines feed (#63) — one line per doc-stated cashflow fact ──────────────────
// Mirrors GET /api/ledger/lines' response shape (see backend/routes/ledger.py):
// only discrete call/distribution facts become lines; commitments, NAV snapshots
// and synthetic events never do. download_url stays null — the demo shim serves
// no blobs; the doc popup renders from filename/blob_name metadata.

const _LINE_TYPE = {
  capital_call: "capital_call_event_amount",
  distribution: "distribution_event_amount",
};

export function ledgerDemoLines() {
  const lines = [];
  for (const pos of POSITIONS) {
    for (const ev of pos.events) {
      const type = _LINE_TYPE[ev.type];
      if (!type || ev.synthetic || ev.amount == null) continue;
      lines.push({
        portal: pos.portal,
        fund: pos.fundName,
        investor: pos.investorName,
        position_key: "",
        event_date: ev.date,
        type,
        amount: ev.amount,
        currency: pos.currency,
        source_doc_id: ev.source_pdf
          ? `documents/${(pos.portal || "").toLowerCase()}/${ev.source_pdf}`
          : null,
        original_filename: ev.source_pdf || null,
        download_url: null,
        event_id: ev.id,
        stream_id: `${pos.portal}|${pos.fundName}|${pos.investorName}`,
        discarded: false,
        excluded: false,
      });
    }
  }
  // Latest first — same ordering contract as lines_from_poc.
  lines.sort((a, b) => (b.event_date || "").localeCompare(a.event_date || ""));
  return { lines, count: lines.length, load_run_id: new Date().toISOString() };
}

// ── ledgerFetch shim ──────────────────────────────────────────────────────────

export async function ledgerFetch(path, init) {
  if (!_demoMode) return fetch(path, init);  // passthrough — preserves existing behavior + tests

  const method = (init?.method || "GET").toUpperCase();
  let data, ok = true, status = 200;

  if (path.startsWith("/api/ledger/overview")) {
    data = ledgerDemoOverview();
  } else if (path.split("?")[0] === "/api/ledger/lines") {
    // Exact match — /api/ledger/lines-export intentionally stays 404 in demo.
    data = ledgerDemoLines();
  } else if (path.startsWith("/api/ledger/timeline")) {
    const params = new URLSearchParams(path.split("?")[1] || "");
    data = ledgerDemoTimeline(params.get("fund_name"), params.get("investor_name"));
  } else if (path.startsWith("/api/ledger/positions/discarded")) {
    data = ledgerDemoDiscarded();
  } else if (path.startsWith("/api/ledger/positions/discard") && method === "POST") {
    data = ledgerDemoDiscard(JSON.parse(init.body));
  } else {
    ok = false; status = 404; data = {};
  }

  return { ok, status, json: async () => data };
}
